import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Link as MuiLink,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../services/supabaseClient';
import { apiService } from '../../services/api';
import { validatePassword, PASSWORD_HELPER_TEXT } from '../../utils/passwordValidation';
import { sanitizeNameInput } from '../../utils/nameInput';

const Register: React.FC = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const validateForm = (): boolean => {
    if (!firstName || !lastName || !email || !password || !confirmPassword) {
      setError('Todos los campos son requeridos');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('El email no es válido');
      return false;
    }
    const passwordErrors = validatePassword(password);
    if (passwordErrors.length > 0) {
      setError(passwordErrors[0]);
      return false;
    }
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return false;
    }
    if (!termsAccepted) {
      setError('Debes aceptar la Política de Privacidad y los Términos de Servicio para continuar');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('public-self-register', {
        body: { firstName, lastName, email, password },
      });

      if (fnError || !data?.success) {
        let msg = 'Error al crear la cuenta';
        try {
          const body = fnError ? await (fnError as any).context?.json() : data;
          msg = body?.message ?? msg;
        } catch {}
        setError(msg);
        return;
      }

      // Auto-login — account is pre-confirmed so this works immediately
      await login(email, password);

      // Fire-and-forget welcome email (session exists after login, so the email service accepts the call)
      apiService.email.sendTesterWelcome({ email, firstName, role: 'admin' }).catch(() => {});

      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Error al crear la cuenta');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#ffffff',
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
            Ston
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Crea tu cuenta de prueba gratuita
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Nombre"
              autoFocus
              value={firstName}
              onChange={(e) => setFirstName(sanitizeNameInput(e.target.value))}
              disabled={isLoading}
              inputProps={{ maxLength: 15, autoComplete: 'off', name: 'ston-nombre' }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Apellido"
              value={lastName}
              onChange={(e) => setLastName(sanitizeNameInput(e.target.value))}
              disabled={isLoading}
              inputProps={{ maxLength: 15, autoComplete: 'off', name: 'ston-apellido' }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Contraseña"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              helperText={PASSWORD_HELPER_TEXT}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Confirmar Contraseña"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
            />

            <FormControlLabel
              sx={{ mt: 2, alignItems: 'flex-start' }}
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  disabled={isLoading}
                  sx={{ pt: 0.5 }}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  He leído y acepto la{' '}
                  <MuiLink href="/privacy" target="_blank">
                    Política de Privacidad
                  </MuiLink>{' '}
                  y los{' '}
                  <MuiLink href="/terms" target="_blank">
                    Términos de Servicio
                  </MuiLink>{' '}
                  <Typography component="span" variant="body2" color="error">*</Typography>
                </Typography>
              }
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                backgroundColor: '#000000',
                '&:hover': { backgroundColor: '#333333' },
              }}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Crear cuenta gratuita'}
            </Button>
          </Box>

          <Box sx={{ mt: 1, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              ¿Ya tienes una cuenta?{' '}
              <MuiLink component={Link} to="/login" sx={{ textDecoration: 'none' }}>
                Inicia sesión
              </MuiLink>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Register;
